import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { useLanguage } from '../context/LanguageContext';
import logger from '../utils/logger';
import 'flag-icons/css/flag-icons.min.css';

const Sidebar = ({ 
  activeSidebarItem, 
  isMobileMenuOpen, 
  onCloseMobileMenu,
  onWebsiteSelect,
  selectedWebsite: propSelectedWebsite, // Receive from parent
  dashboardType = 'admin' // 'admin' for Admin, 'streamer' for PIMP MY DROP
}) => {
  const { admin, logout } = useAdmin();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const navigate = useNavigate();
  
  // Website dropdown state (only for admin dashboard)
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(propSelectedWebsite || '');
  const [showWebsiteDropdown, setShowWebsiteDropdown] = useState(false);
  const [loadingWebsites, setLoadingWebsites] = useState(false);

  // Language dropdown state
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef(null);

  // Available languages
  const languages = [
    { code: 'en', name: 'English', flag: 'fi fi-us' },
    { code: 'fr', name: 'Français', flag: 'fi fi-fr' }
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Language selection handlers
  const handleLanguageSelect = (languageCode) => {
    changeLanguage(languageCode);
    setShowLanguageDropdown(false);
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[0];
  };

  // Handle sidebar navigation
  const handleSidebarNavigation = (item, tabOrPath = null) => {
    // Close mobile menu when navigating
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
    
    // If it's a route path (starts with /), navigate to it
    if (tabOrPath && tabOrPath.startsWith('/')) {
      navigate(tabOrPath);
    } else if (tabOrPath) {
      // Navigate based on dashboard type - use correct dashboard path
      if (dashboardType === 'streamer') {
        navigate(`/streamer/dashboard?tab=${tabOrPath}`);
      } else {
        navigate(`/admin/dashboard?tab=${tabOrPath}`);
      }
    } else {
      // Default navigation based on dashboard type
      if (dashboardType === 'streamer') {
        navigate('/streamer/dashboard');
      } else {
        navigate('/admin/dashboard');
      }
    }
  };

  // Fetch websites from admin table
  const fetchWebsites = async () => {
    setLoadingWebsites(true);
    try {
      const response = await fetch('/.netlify/functions/get-websites');
      if (response.ok) {
        const data = await response.json();
        const websiteList = data.websites || [];
        setWebsites(Array.isArray(websiteList) ? websiteList : []);
        logger.log('✅ Fetched websites:', websiteList);
      } else {
        logger.error('Failed to fetch websites, status:', response.status);
        const errorText = await response.text();
        logger.error('Error details:', errorText);
        setWebsites([]);
      }
    } catch (error) {
      logger.error('Error fetching websites:', error);
      setWebsites([]);
    } finally {
      setLoadingWebsites(false);
    }
  };

  // Load websites on component mount (only for admin dashboard)
  useEffect(() => {
    if (dashboardType === 'admin') {
      fetchWebsites();
    }
  }, [admin, dashboardType]);

  // Expose fetchWebsites function globally for refreshing after approval
  useEffect(() => {
    if (dashboardType === 'admin') {
      window.refreshWebsiteList = fetchWebsites;
    }
    
    // Cleanup on unmount
    return () => {
      if (window.refreshWebsiteList === fetchWebsites) {
        delete window.refreshWebsiteList;
      }
    };
  }, [dashboardType]);

  // Sync selectedWebsite with prop from parent
  useEffect(() => {
    if (propSelectedWebsite !== undefined && propSelectedWebsite !== selectedWebsite) {
      setSelectedWebsite(propSelectedWebsite);
    }
  }, [propSelectedWebsite, selectedWebsite]);

  // Click outside handler for language dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set initial selected website from URL or admin's owner (only for admin dashboard)
  useEffect(() => {
    if (dashboardType === 'admin') {
      const urlParams = new URLSearchParams(window.location.search);
      const websiteFromUrl = urlParams.get('website');
      
      if (websiteFromUrl && !selectedWebsite) {
        // Find the matching website in the websites array
        const matchingWebsite = websites.find(site => site === websiteFromUrl);
        if (matchingWebsite) {
          setSelectedWebsite(matchingWebsite);
        } else {
          setSelectedWebsite(websiteFromUrl);
        }
      } else if (admin?.owner && !selectedWebsite && !websiteFromUrl) {
        // Don't auto-select admin, let user choose manually
        if (admin.owner !== 'admin') {
          // Find the matching website in the websites array
          const matchingWebsite = websites.find(site => site === admin.owner);
          if (matchingWebsite) {
            setSelectedWebsite(matchingWebsite);
            if (onWebsiteSelect) {
              onWebsiteSelect(admin.owner);
            }
          }
        }
      }
    }
  }, [admin, selectedWebsite, onWebsiteSelect, dashboardType, websites]);

  // Toggle website dropdown
  const toggleWebsiteDropdown = () => {
    setShowWebsiteDropdown(!showWebsiteDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWebsiteDropdown && !event.target.closest('.website-dropdown')) {
        setShowWebsiteDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showWebsiteDropdown]);

  // Handle website selection
  const handleWebsiteSelect = (website) => {
    setSelectedWebsite(website); // Use website name directly
    setShowWebsiteDropdown(false);
    
    // For admin dashboard, navigate directly to the proper URL using website name
    if (dashboardType === 'admin') {
      const url = `/admin/dashboard?tab=overview&website=${encodeURIComponent(website)}`;
      navigate(url);
      logger.log('🌐 Navigating to:', url);
    }
    
    // Call parent callback to handle website selection (for other logic)
    if (onWebsiteSelect) {
      onWebsiteSelect(website);
    }
    logger.log('Selected website:', website);
  };

  return (
    <div className={`
      ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      lg:translate-x-0 lg:static fixed inset-y-0 left-0 z-50
      w-64 bg-gray-800 flex flex-col h-full
      transition-transform duration-300 ease-in-out
    `}>
      {/* Mobile Close Button */}
      <div className="lg:hidden flex justify-end p-4">
        <button
          onClick={onCloseMobileMenu}
          className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Logo */}
      <div className="p-6 border-b border-gray-700">
        <h1 
          className="text-2xl font-bold text-pink-500 cursor-pointer hover:text-pink-400 transition-colors duration-200"
          onClick={() => {
            if (dashboardType === 'streamer') {
              handleSidebarNavigation('dashboard', 'challenges');
            } else {
              // Always navigate to general admin dashboard without website parameter
              navigate('/admin/dashboard?tab=overview');
            }
            if (onCloseMobileMenu) {
              onCloseMobileMenu();
            }
          }}
          title={t('backToDashboard')}
        >
          {dashboardType === 'streamer' ? 'PIMP MY DROP' : 'Admin'}
        </h1>
      </div>

      {/* Navigation - Flexible grow */}
      <nav className="flex-1 p-6 space-y-6 overflow-y-auto sidebar-scroll">
        {dashboardType === 'streamer' ? (
          // Streamer Dashboard Navigation (Original PIMP MY DROP style)
          <>
            {/* GÉNÉRAL Section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('general')}</h3>
              <div className="space-y-2">
                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    activeSidebarItem === 'dashboard' ? 'bg-gray-700' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => handleSidebarNavigation('dashboard', 'challenges')}
                >
                  <i className="fas fa-home text-blue-500 w-5"></i>
                  <span className="font-medium">{t('dashboard')}</span>
                </div>
                
                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    activeSidebarItem === 'soumissions' ? 'bg-gray-700' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => handleSidebarNavigation('soumissions', 'validation')}
                >
                  <i className="fas fa-file-alt text-green-500 w-5"></i>
                  <span>{t('submissions')}</span>
                </div>

                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    activeSidebarItem === 'challenges' ? 'bg-gray-700' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => handleSidebarNavigation('challenges', '/streamer/challenges-list')}
                >
                  <i className="fas fa-trophy text-purple-500 w-5"></i>
                  <span>{t('challenges')}</span>
                </div>
              </div>
            </div>

            {/* ADMINISTRATION Section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('administration')}</h3>
              <div className="space-y-2">
                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    activeSidebarItem === 'settings' ? 'bg-gray-700' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => handleSidebarNavigation('settings', '/streamer/settings')}
                >
                  <i className="fas fa-cog text-gray-400 w-5"></i>
                  <span>{t('settings')}</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          // Admin Dashboard Navigation
          <>
            {/* GÉNÉRAL Section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('general')}</h3>
              <div className="space-y-2">
                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    activeSidebarItem === 'dashboard' ? 'bg-gray-700' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => {
                    // Always navigate to general dashboard without website parameter
                    navigate('/admin/dashboard?tab=overview');
                    if (onCloseMobileMenu) {
                      onCloseMobileMenu();
                    }
                  }}
                >
                  <i className="fas fa-home text-blue-500 w-5"></i>
                  <span className="font-medium">{t('dashboard')}</span>
                </div>
              </div>
            </div>

            {/* Website Dropdown Section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('website')}</h3>
              <div className="relative website-dropdown">
                <button
                  onClick={toggleWebsiteDropdown}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all duration-200 text-left ${
                    showWebsiteDropdown 
                      ? 'bg-blue-600 border-blue-500 shadow-lg' 
                      : 'bg-gray-700 border-gray-600 hover:bg-gray-600 hover:border-gray-500'
                  }`}
                  disabled={loadingWebsites}
                >
                                      <div className="flex items-center space-x-3 min-w-0">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        showWebsiteDropdown ? 'bg-blue-500' : 'bg-green-600'
                      }`}>
                        <i className="fas fa-globe text-white text-xs"></i>
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="text-xs text-gray-300 mb-1">
                          {selectedWebsite ? t('selectedWebsite') : t('selectWebsite')}
                        </div>
                        <div className="font-medium text-white">
                          {loadingWebsites ? (
                            <div className="flex items-center space-x-2">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              <span>{t('loading')}</span>
                            </div>
                          ) : selectedWebsite ? (
                            <div className="flex items-center space-x-2 min-w-0">
                              <span className="text-green-400 text-xs flex-shrink-0">●</span>
                              <span className="truncate block min-w-0">{selectedWebsite}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 truncate block">{t('chooseWebsite')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  <svg 
                    className={`w-4 h-4 transition-transform duration-200 text-gray-300 ${
                      showWebsiteDropdown ? 'rotate-180' : ''
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
                
                {showWebsiteDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl z-50 max-h-64 overflow-hidden">
                    {/* Dropdown Header */}
                    <div className="px-4 py-3 bg-gray-700 border-b border-gray-600">
                      <div className="flex items-center space-x-2">
                        <i className="fas fa-globe text-blue-400 w-4"></i>
                        <span className="text-sm font-medium text-white">{t('selectWebsite')}</span>
                      </div>
                    </div>
                    
                    {/* Dropdown Content */}
                    <div className="py-2 max-h-48 overflow-y-auto sidebar-scroll">
                      {websites.length === 0 ? (
                        <div className="px-4 py-6 text-center">
                          <div className="text-gray-400 mb-2">
                            <i className="fas fa-exclamation-circle text-2xl"></i>
                          </div>
                          <div className="text-sm text-gray-400">
                            {loadingWebsites ? t('loadingWebsites') : t('noWebsitesFound')}
                          </div>
                        </div>
                      ) : (
                        websites.map((website, index) => (
                          <button
                            key={index}
                            onClick={() => handleWebsiteSelect(website)}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-700 transition-all duration-150 group ${
                              selectedWebsite === website ? 'bg-blue-600 hover:bg-blue-700' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 min-w-0">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  selectedWebsite === website ? 'bg-blue-500' : 'bg-gray-600 group-hover:bg-gray-500'
                                }`}>
                                  <i className="fas fa-globe text-white text-xs"></i>
                                </div>
                                <div className="min-w-0">
                                  <div className={`text-sm font-medium truncate ${
                                    selectedWebsite === website ? 'text-white' : 'text-gray-200'
                                  }`}>
                                    {website}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {t('websiteDomain')}
                                  </div>
                                </div>
                              </div>
                              {selectedWebsite === website && (
                                <div className="text-green-400">
                                  <i className="fas fa-check text-sm"></i>
                                </div>
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

          {/* Shopify Sidebar Section */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('shopify')}</h3>
            <div className="space-y-2">
              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'orders' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('orders', '/admin/orders')}
              >
                <span className="text-lg">📦</span>
                <span className="font-medium">{t('orders')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'products' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('products', '/admin/store')}
              >
                <span className="text-lg">🛍️</span>
                <span className="font-medium">{t('products')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'customers' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('customers', '/admin/customers')}
              >
                <span className="text-lg">👥</span>
                <span className="font-medium">{t('customers')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'analytics' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('analytics', 'analytics')}
              >
                <span className="text-lg">📊</span>
                <span className="font-medium">{t('analytics')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'marketing' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('marketing')}
              >
                <span className="text-lg">📢</span>
                <span className="font-medium">{t('marketing')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'discounts' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('discounts')}
              >
                <span className="text-lg">🏷️</span>
                <span className="font-medium">{t('discounts')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'apps' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('apps')}
              >
                <span className="text-lg">🧩</span>
                <span className="font-medium">{t('apps')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'online-store' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('online-store')}
              >
                <span className="text-lg">🌐</span>
                <span className="font-medium">{t('onlineStore')}</span>
              </div>

              <div 
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  activeSidebarItem === 'settings' ? 'bg-gray-700' : 'hover:bg-gray-700'
                }`}
                onClick={() => handleSidebarNavigation('settings')}
              >
                <span className="text-lg">⚙️</span>
                <span className="font-medium">{t('settings')}</span>
              </div>
            </div>
          </div>
          </>
        )}
      </nav>

      {/* User Info - Fixed at bottom */}
      <div className="p-6 border-t border-gray-700 bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div>
              <p className="text-sm text-gray-400">
                {dashboardType === 'streamer' 
                  ? (
                    <>
                      {t('loggedInAs')} <span className="font-bold text-white">{admin?.owner || 'Streamer'}</span>
                    </>
                  )
                  : t('loggedInAs')
                }
              </p>
              <p className="font-medium flex items-center space-x-2">
                <span className="relative inline-block" ref={languageDropdownRef}>
                  <button
                    onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                    className="inline-flex items-center space-x-1 p-1 text-gray-300 hover:text-white transition-colors"
                    title={t('selectLanguage')}
                  >
                    <span className={`${getCurrentLanguage().flag} text-xs`}></span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showLanguageDropdown && (
                    <div className="absolute bottom-full mb-2 left-0 bg-gray-800 border border-gray-600 rounded-md shadow-lg py-1 min-w-[120px] z-50">
                      {languages.map((language) => (
                        <button
                          key={language.code}
                          onClick={() => handleLanguageSelect(language.code)}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-700 transition-colors flex items-center space-x-2 ${
                            currentLanguage === language.code ? 'text-cyan-400' : 'text-gray-300'
                          }`}
                        >
                          <span className={`${language.flag} text-xs`}></span>
                          <span>{language.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </span>
                <span>
                  {dashboardType === 'streamer' 
                    ? (admin?.name || 'User')  // Show actual user name for streamers
                    : (admin?.name || 'Admin') // Show admin name for admin
                  }
                </span>
              </p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="text-red-400 hover:text-red-300 p-2 hover:bg-gray-700 rounded-lg transition-colors"
            title={t('signOut')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 